import { ref, watch } from 'vue'
import L from 'leaflet'
import {
  STREET_LAYER, DARK_STREET_LAYER, SATELLITE_LAYER, GOOGLE_STREET, GOOGLE_SATELLITE,
  STREET_LAYER_CLEAN, GOOGLE_STREET_CLEAN, GOOGLE_SATELLITE_CLEAN,
} from '@/store/mapConstants'
import { useMapPrefs } from '@/composables/useMapPrefs'
import { useThemeStore } from '@/store/useThemeStore'
import { usePublicTheme } from '@/composables/usePublicTheme'

// `local: true` desacopla este mapa de la preferencia global persistida:
// arranca siempre en calles/no-Google y el toggle sólo dura la sesión.
// Se usa en las vistas públicas, para que no herede la preferencia de Google
// ni el modo oscuro guardados por el panel admin en el mismo navegador (las
// páginas públicas arrancan siempre en claro, ver usePublicTheme).
//
// `satellite` (ref opcional) permite que una vista maneje su propio estado
// calles/satélite, sin leer ni persistir la preferencia global.
//
// `clean` (ref opcional) activa la variante "limpia" del mapa: sólo calles,
// sin negocios ni puntos de interés.
export function useMapTiles(leafletMapGetter, { local = false, satellite = null, clean = null } = {}) {
  const prefs = useMapPrefs()
  const themeStore = local ? usePublicTheme() : useThemeStore()
  const googleEnabled = local ? ref(false) : prefs.googleEnabled
  const setGoogleEnabled = local ? (val) => { googleEnabled.value = !!val } : prefs.setGoogleEnabled
  const satelliteEnabled = satellite ?? (local ? ref(false) : prefs.satelliteEnabled)
  const setSatelliteEnabled = (satellite || local)
    ? (val) => { satelliteEnabled.value = !!val }
    : prefs.setSatelliteEnabled
  let tileLayer = null

  const _addTile = (map, config) => {
    if (tileLayer) { tileLayer.remove(); tileLayer = null }
    tileLayer = L.tileLayer(config.url, {
      attribution: config.attribution,
      maxZoom:     config.maxZoom    ?? 19,
      tileSize:    config.tileSize   ?? 256,
      zoomOffset:  config.zoomOffset ?? 0,
      subdomains:  config.subdomains ?? 'abc',
    }).addTo(map)
    return tileLayer
  }

  // Si la preferencia global de Google está activa, se usan los tiles de Google
  // en lugar de los presets por defecto (aplica a todos los mapas públicos).
  // El callejero cambia a su variante oscura cuando la app está en modo oscuro;
  // el satélite y Google no tienen variante oscura y quedan igual.
  const limpio = () => !!clean?.value
  const addStreetTiles = (map) =>
    _addTile(map, googleEnabled.value
      ? (limpio() ? GOOGLE_STREET_CLEAN : GOOGLE_STREET)
      : (themeStore.theme === 'dark' ? DARK_STREET_LAYER : (limpio() ? STREET_LAYER_CLEAN : STREET_LAYER)))
  const addSatelliteTiles = (map) =>
    _addTile(map, googleEnabled.value
      ? (limpio() ? GOOGLE_SATELLITE_CLEAN : GOOGLE_SATELLITE)
      : SATELLITE_LAYER)

  // Aplica la capa según las preferencias actuales (usar al crear el mapa).
  const applyCurrentTiles = (map) =>
    satelliteEnabled.value ? addSatelliteTiles(map) : addStreetTiles(map)

  // Re-aplica la capa actual (calles/satélite) respetando la preferencia de Google.
  const refresh = () => {
    const map = leafletMapGetter?.()
    if (!map) return
    applyCurrentTiles(map)
  }

  // Si el usuario alterna modo oscuro con el mapa ya abierto, re-aplica la
  // capa de calles para reflejar el cambio (el satélite no se ve afectado).
  watch(() => themeStore.theme, () => {
    if (!satelliteEnabled.value) refresh()
  })

  // Con un estado propio, cualquier cambio (p. ej. desde otro mapa de la misma
  // vista que comparte el ref) se refleja en este mapa.
  if (satellite) watch(satellite, refresh)
  if (clean) watch(clean, refresh)

  // Alterna calles/satélite (persistido) y re-aplica al mapa activo.
  const toggle = () => {
    const map = leafletMapGetter?.()
    if (!map) return
    setSatelliteEnabled(!satelliteEnabled.value)
    applyCurrentTiles(map)
  }

  // Activa/desactiva Google globalmente y re-aplica los tiles al mapa activo.
  const toggleGoogle = () => {
    setGoogleEnabled(!googleEnabled.value)
    refresh()
  }

  return {
    isSatellite: satelliteEnabled,
    googleEnabled,
    addStreetTiles,
    addSatelliteTiles,
    applyCurrentTiles,
    toggle,
    refresh,
    toggleGoogle,
  }
}
